import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { SuccessDialog } from "@/components/survey/SuccessDialog";
import { LoadingState } from "@/components/survey/LoadingState";
import { ErrorState } from "@/components/survey/ErrorState";
import { SurveyContent } from "@/components/survey/SurveyContent";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useTheme } from "@/components/ui/theme-provider";

interface Question {
  id: string;
  question: string;
  options?: string[];
  question_type: string;
}

interface WaitlistCount {
  id: string;
  count: number;
}

const loadSavedProgress = () => {
  try {
    const savedProgress = localStorage.getItem('surveyProgress');
    if (savedProgress) {
      const data = JSON.parse(savedProgress);
      // Check if data is expired (older than 24 hours)
      if (data.timestamp && Date.now() - data.timestamp > 24 * 60 * 60 * 1000) {
        localStorage.removeItem('surveyProgress');
        return null;
      }
      return data;
    }
    return null;
  } catch (error) {
    console.error('Error loading saved progress:', error);
    localStorage.removeItem('surveyProgress');
    return null;
  }
};

export default function Survey() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { theme } = useTheme();
  const [currentQuestion, setCurrentQuestion] = useState(() => {
    const savedData = loadSavedProgress();
    return savedData?.currentQuestion ?? 0;
  });
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Record<string, any>>(() => {
    const savedData = loadSavedProgress();
    return savedData?.answers ?? {};
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState(() => {
    const savedData = loadSavedProgress();
    return savedData?.profile ?? {
      fullName: "",
      company: "",
      email: "",
      telephone: "",
      contactConsent: false,
      privacyConsent: false,
      logoConsent: false,
    };
  });
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('questions')
        .select('*')
        .order('order_number');

      if (fetchError) throw fetchError;

      // Add console.log to debug the data
      console.log('Fetched questions:', data);

      if (!data || data.length === 0) {
        setError("No questions available in the database. Please contact support.");
        return;
      }

      const transformedQuestions: Question[] = [
        { id: '0', question: 'Profile', question_type: 'profile' },
        ...data.map(q => ({
          id: q.id,
          question: q.question,
          options: q.options as string[] || [],
          question_type: q.question_type || 'radio'
        }))
      ];

      setQuestions(transformedQuestions);
      setError(null);
    } catch (error: any) {
      console.error('Error fetching questions:', error);
      setError(error.message || "Failed to load survey questions");
      toast({
        title: "Error",
        description: "Failed to load survey questions. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleProfileChange = (field: string, value: string | boolean) => {
    setProfile(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const saveToSupabase = async () => {
    try {
      console.log('Starting saveToSupabase...');
      console.log('Profile data:', profile);
      console.log('Answers:', answers);
      console.log('Questions:', questions);

      const { data: existingUser, error: checkError } = await supabase
        .from('waitlist')
        .select('id')
        .eq('email', profile.email)
        .maybeSingle();

      if (checkError) {
        console.error('Error checking existing user:', checkError);
        throw checkError;
      }

      let waitlistEntry;

      if (existingUser) {
        console.log('User already exists, updating profile...');
        // Update existing waitlist entry with new profile data
        const { error: updateError } = await supabase
          .from('waitlist')
          .update({
            full_name: profile.fullName,
            company: profile.company,
            telephone: profile.telephone,
            contact_consent: profile.contactConsent,
            privacy_policy_accepted: profile.privacyConsent,
            logo_consent: profile.logoConsent
          })
          .eq('id', existingUser.id);

        if (updateError) {
          console.error('Error updating waitlist entry:', updateError);
          throw updateError;
        }

        waitlistEntry = existingUser; // Use the existing user data
        console.log('Waitlist entry updated:', waitlistEntry);
      } else {
        console.log('Creating new waitlist entry...');
        // Create new waitlist entry
        const { data: newEntry, error: waitlistError } = await supabase
          .from('waitlist')
          .insert({
            full_name: profile.fullName,
            company: profile.company,
            email: profile.email,
            telephone: profile.telephone,
            contact_consent: profile.contactConsent,
            privacy_policy_accepted: profile.privacyConsent,
            logo_consent: profile.logoConsent
          })
          .select()
          .single();

        if (waitlistError) {
          console.error('Error creating waitlist entry:', waitlistError);
          throw waitlistError;
        }

        waitlistEntry = newEntry;
        console.log('Waitlist entry created:', waitlistEntry);
      }

      // Check if user already has survey responses
      const { data: existingResponses, error: responsesCheckError } = await supabase
        .from('survey_responses')
        .select('id')
        .eq('waitlist_id', waitlistEntry.id);

      if (responsesCheckError) {
        console.error('Error checking existing responses:', responsesCheckError);
        throw responsesCheckError;
      }

      if (existingResponses && existingResponses.length > 0) {
        console.log('User already has survey responses, updating...');
        // Delete existing responses and insert new ones
        const { error: deleteError } = await supabase
          .from('survey_responses')
          .delete()
          .eq('waitlist_id', waitlistEntry.id);

        if (deleteError) {
          console.error('Error deleting existing responses:', deleteError);
          throw deleteError;
        }
      }

      // Filter out the profile question (id: '0') and map the responses
      const surveyResponses = Object.entries(answers)
        .filter(([questionId]) => questionId !== '0')
        .map(([questionId, answer]) => {
          // Find the question to verify it exists
          const question = questions.find(q => q.id === questionId);
          if (!question) {
            console.error(`Question with ID ${questionId} not found in questions array`);
            return null;
          }

          return {
            waitlist_id: waitlistEntry.id,
            question_id: questionId,
            answer: Array.isArray(answer) ? answer.join(', ') : answer.toString(),
          };
        })
        .filter(Boolean); // Remove any null entries

      console.log('Preparing survey responses:', surveyResponses);

      if (surveyResponses.length > 0) {
        const { error: responsesError } = await supabase
          .from('survey_responses')
          .insert(surveyResponses);

        if (responsesError) {
          console.error('Error saving survey responses:', responsesError);
          throw responsesError;
        }
      }

      // Only increment count if this is a new user
      if (!existingUser) {
        console.log('Incrementing waitlist count...');
        const { error: incrementError } = await supabase.rpc('increment_waitlist_count');

        if (incrementError) {
          console.error('Error incrementing count:', incrementError);
          throw incrementError;
        }
      }

      console.log('Survey submission complete');
      toast({
        title: "Success!",
        description: existingUser 
          ? "Your profile has been updated and survey responses saved."
          : "Your responses have been saved.",
        variant: "default",
      });
      
      localStorage.removeItem('surveyProgress');
      localStorage.removeItem('hasSeenWelcome');
      
      setShowSuccess(true);
    } catch (error: any) {
      console.error('Error in saveToSupabase:', error);
      toast({
        title: "Error",
        description: error.message || "There was a problem saving your responses. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleAnswer = (value: string | string[]) => {
    const currentQuestionData = questions[currentQuestion];
    setAnswers(prev => ({
      ...prev,
      [currentQuestionData.id]: value,
    }));
  };

  const handleNext = async () => {
    const currentQuestionData = questions[currentQuestion];
    const currentAnswer = answers[currentQuestionData.id];
    
    console.log('Current question:', currentQuestion);
    console.log('Total questions:', questions.length);
    console.log('Current answer:', currentAnswer);
    
    // Only validate answer if we're moving to the next question (not submitting)
    if (currentQuestion < questions.length - 1) {
      if (!currentAnswer || (Array.isArray(currentAnswer) && currentAnswer.length === 0)) {
        toast({
          title: "Required",
          description: "Please provide an answer before continuing",
          variant: "destructive",
        });
        return;
      }
      console.log('Moving to next question');
      setCurrentQuestion(prev => prev + 1);
    } else {
      console.log('Submitting survey...');
      try {
        await saveToSupabase();
        console.log('Survey submitted successfully');
      } catch (error) {
        console.error('Error submitting survey:', error);
      }
    }
  };

  // This function is no longer needed since validation is handled in ProfileForm
  const handleProfileSubmit = () => {
    // Pass control to the ProfileForm's validation
    setCurrentQuestion(prev => prev + 1);
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  if (isLoading) {
    return <LoadingState />;
  }

  if (error || !questions.length) {
    return <ErrorState message={error || "No questions available"} />;
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-2xl mx-auto relative">
        <SurveyContent
          currentQuestion={currentQuestion}
          questions={questions}
          answers={answers}
          profile={profile}
          onProfileChange={handleProfileChange}
          onAnswer={handleAnswer}
          onBack={handleBack}
          onNext={handleNext}
          onProfileSubmit={handleProfileSubmit}
        />

        <SuccessDialog
          open={showSuccess}
          onOpenChange={(open) => {
            if (!open) {
              navigate("/");
            }
          }}
        />
      </div>
    </div>
  );
}